import moment from "moment";
import { Table } from "antd";
import _ from "lodash";
import { formatAmount, dateFormatter } from "../../../../utils/commonUtil";
import { checkCSRBusinessFunction } from "../../../../CSR/utils/CommonUtils";
import { BUSINESS_FUNCTION } from "../../../../CSR/utils/csrBusinessFunctionMapper";

export const recognitionMap = {
  "10": "Account Advisor name(s), address(es), and philanthropic account name",
  "20":
    "Account advisor name(s), address(es), and philanthropic account name, and a third party (individual or organization).",
  "30": "Anonymous",
  "40": "Account Name only"
};

export const recognitionType = {
  "10": "Account Advisors",
  "20": "Third Party",
  "30": "Anonymous",
  "40": "Account name"
};

export const grantLetterFormatMap = {
  "10": "GAN",
  "20": "GAN",
  "30": "GAA",
  "40": "GAO"
};

export const validationRuleStateChecker = value => ({
  //Should be changed to Common validator
  lessThanValue:
    value === "Monthly"
      ? 600
      : value === "Quarterly"
        ? 200
        : value === "Annually"
          ? 50
          : value === "Semiannually"
            ? 100
            : 0
});


export const messageStateChecker = value => ({
  //Should be changed to Common validator
  lessThanValue:
    value === "Monthly"
      ? "The maximum number of payments for a monthly recurring granting schedule is 600."
      : value === "Quarterly"
        ? "The maximum number of payments for a quarterly recurring granting schedule is 200."
        : value === "Annually"
          ? "The maximum number of payments for an annual recurring granting schedule is 50."
          : value === "Semiannually"
            ? "The maximum number of payments for semi-annual recurring granting schedule is 100."
            : ""
});

const typeMap = {
  weightedAllocations: "By weighted percentage",
  byDollars: "By percentage or dollar amount",
  byPercentage: "By specific percentage",
  perDollarAmount: "By specific dollar amount"
};

const frequencyMapping = {
  Monthly: 1,
  Quarterly: 3,
  Semiannually: 6,
  Annually: 12
};

const createPurposeRecognitionRender = props => {
  const {
    purposeName,
    additionalComments,
    inHonorOfName,
    inMemoryOfName,
    notifyFirstName,
    notifyLastName,
    grantRecognition,
    advisors,
    thirdParty,
    includeAdvisorInfo,
    accountName,
    eventName,
    missionaryName,
    missionaryLocation,
    projectName,
  } = props;
  let notifyName;
  if (notifyFirstName && notifyLastName) {
    notifyName = `${notifyFirstName} ${notifyLastName}`;
  }
  return {
    title: "Grant purpose & recognition",
    purposeName,
    additionalComments,
    inHonorOfName: inHonorOfName && `In honor of ${inHonorOfName}`,
    inMemoryOfName: inMemoryOfName && `In memory of ${inMemoryOfName}`,
    grantRecognition,
    notifyName,
    advisors,
    includeAdvisorInfo,
    thirdParty,
    accountName,
    eventName,
    missionaryName,
    missionaryLocation,
    projectName,
  };
};

export const calEndDate = (startDate, payment, frequency) => {
  const monthCal = (payment - 1) * frequencyMapping[frequency];
  const fm = moment(startDate)
    .add(monthCal, "M")
    .format("L");
  return fm;
};

const calcGrantTotal = (spawns, grantAmount, noOfPayments) => {
  if (spawns && spawns.length) return spawns.reduce((a, b)=> a += parseFloat(b.amount), 0);
  return parseFloat(grantAmount) * parseInt(noOfPayments, 10);
};

const createScheduleRender = props => {
  const {
    grantAmount,
    scheduleType,
    frequency,
    startDate,
    noOfPayments,
    endDate,
    amountType,
    allocations,
    allSharesAllPools,
    allShares,
    spawns
  } = props;

  let recurring = false;
  let amountString = `${formatAmount(grantAmount)}`;
  if (scheduleType === "recurring") {
    recurring = true;
    const totalAmount = calcGrantTotal(spawns, grantAmount, noOfPayments);
    amountString = `${noOfPayments} for a total of ${formatAmount(
      totalAmount
    )}`;
  }
  return {
    title: "Recommended amount & allocation",
    recurring,
    paymentType: recurring ? "Recurring Payment" : "One Time Payment",
    frequency,
    amountString,
    startDate:
      startDate ||
      "As Soon as Possible (Most grants will take 5-7 business days)",
    endDate: endDate || calEndDate(startDate, noOfPayments, frequency),
    allocationType: amountType,
    allocations,
    allSharesAllPools,
    allShares
  };
};

const prepareRender = props => {
  const {
    charityName,
    charityAddress,
    charityEin,
    grantAmount,
    publishName,
    frequency,
    startDate,
    noOfPayments,
    scheduleType,
    codeValueMappingData
  } = props;

  const charityInfo = {
    title: "Charity Information",
    charityName,
    charityAddress,
    charityEin
  };
  const purposeRecognition = createPurposeRecognitionRender(props);
  const amountAllocation = createScheduleRender(props);

  return {
    charityName,
    grantAmount,
    publishName,
    frequency,
    startDate,
    noOfPayments,
    scheduleType,
    charityInfo,
    purposeRecognition,
    amountAllocation,
    scheduleData: {
      frequency,
      noOfPayments,
      startDate,
      amount: grantAmount,
      grantName: charityName,
      Table
    },
    codeValueMappingData
  };
};

export const findKeyForValue = (lookup, object) => {
  let result;
  for (let key in object) {
    if (object[key].toUpperCase() === lookup.toUpperCase()) {
      result = key;
    }
  }
  return result;
};

export const reverseMapAllocationType = allocationPreference => {
  let allocationType;
  for (let type in typeMap) {
    if (typeMap[type].toUpperCase() === allocationPreference.toUpperCase()) {
      allocationType = type;
    }
  }
  return allocationType;
};

const allSharesTypeContent = "Entire Balance of specified pool(s)";
const allSharesAllPoolsTypeContent = "Entire Balance of all pools";

export const mapAllocationTypeLabel = (allocationType, allSharesAllPoolsType, allSharesType) => {
  if (allSharesAllPoolsType) {
    return allSharesAllPoolsTypeContent;
  } else if (allSharesType) {
    return allSharesTypeContent;
  }
  return typeMap[allocationType];
}

export const createGrantAllocInput = (allocations, allocationType) => {
  return allocations.map(alloc => ({
    position: {
      pool: {
        poolId: alloc.id
      }
    },
    percentageAllocated:
      allocationType === "byPercentage" ? alloc.grantPercentage : null,
    amountAllocated: (allocationType === "byDollars" || allocationType === "perDollarAmount" || allocationType === "entireBalance") ? alloc.grantAmount : null,
    weightedPercenatgePerPool:
      allocationType === "weightedAllocations" ? alloc.grantAmount : null,
    allocatedUnits: alloc.allShare ? alloc.units : null
  }));
};

export const mapGrantType = scheduleType => {
  if (scheduleType === "oneTime") {
    return "singleTime";
  }
  return "multipleTime";
};

// if asap requestToday else reqDateFuture
export const getGrantDateType = startDate => {
  if (!startDate || moment(startDate).isSame(moment(), "day")) {
    return "reqToday";
  }
  return "reqDateFuture";
};

// Without ParseFloat Getting Invalid Date
export const formatter = date => dateFormatter(parseFloat(date));

export const spawnFormatter = val =>
  val &&
  val.map((item, i) => ({
    grantName: item.charityDetails.charityName + " | " + parseFloat(i + 1),
    spawnId: item.spawnId,
    amount: item.amount,
    scheduledDate: item.issueDateStr,
    issueDate: item.checkIssuedDate
      ? formatter(item.checkIssuedDate)
      : item.checkIssuedDate,
    checkNumber: item.checkNumber,
    status: item.status,
    grantAllocation: item.grantAllocation,
    allocationType: item.allocationType
  }));

export const FrequencyMapping = freq => {
  let frequency = { Monthly: 1, Quarterly: 3, Semiannually: 6, Annually: 12 };
  return frequency[freq];
};

export const arrayChecker = (newArr, prevArr) => {
  return _(newArr)
    .differenceWith(prevArr, _.isEqual)
    .isEmpty();
};

export const calScheduledDate = (date, monthsToAdd) => {
  return moment(date)
    .add(monthsToAdd, "M")
    .format("MM/DD/YYYY");
};

export const statusChecker = (status) => {
  if (
    status.toLowerCase() === "not scheduled" ||
    status.toLowerCase() === "entered" ||
    status.toLowerCase() === "research proceeding"
  )
    return true;
  return false;
};

export const spawnAccessCheck = (status) => status.toLowerCase() === "not scheduled";
export const checkSpawnEdit = (status,scheduleStatus,csrBusinessFunction)=>{ 
 let csrCheck =  checkCSRBusinessFunction(
    BUSINESS_FUNCTION[
      'DONOR_PORTAL_EDIT_RECURRING_GRANT_SCHEDULE_FULL_ACCESS'
    ],
    csrBusinessFunction
  )
  return scheduleStatus === "Active" && spawnAccessCheck(status) && !csrCheck
};

export const allocationCalculator = (allocations, allocationPref, grantAmount) => {
  // if allocation pref is by amount check if amount is equal
  if (allocationPref === "byDollars" || allocationPref=== "byPercentage") {
    const totalAmount = allocations.reduce(
      (a, b) => a + parseFloat(b.grantAmount),
      0
    );
    return Number(totalAmount).toFixed(2) === Number(grantAmount).toFixed(2);
  }
  const totalPercent = allocations.reduce(
    (a, b) => a + parseFloat(b.grantPercentage),
    0
  );
  // if allocation preference equals weighted allocation, then check percent for approx
  if (allocationPref === "weightedAllocations" || allocationPref === "entireBalance") {
    const approxPercent =
      totalPercent > 100 ? Math.floor(totalPercent) : Math.ceil(totalPercent);
    return approxPercent === 100;
  }
  // entireBalance allocation always 100%
  // if(allocationPref === "entireBalance") {
  //   return true
  // }
  // else check actual
  return totalPercent === 100;
};

export const addressFormat = ({
  addressLine1,
  addressLine2,
  addressLine3,
  city,
  state,
  zipCode,
}) => {
  return `${addressLine1}, ${addressLine2 ? addressLine2 + "," : ""} ${addressLine3 ? addressLine3 + "," : ""} ${city ? city + "," : ""} ${state || ""} ${zipCode}`;
};

export const getNameWithSalutation = (firstName, middleName, lastName, codeValueMappingData, title, suffix) => {
  const salutation  = codeValueMappingData.title.filter( (item) => { return item.value === title; });
  const designation  = codeValueMappingData.suffix.filter( (item) => { return item.value === suffix; });

  if(salutation.length !==0 &&  designation.length !==0 ) {
    return `${salutation[0].desc} ${firstName} ${(middleName? middleName: ' ')} ${lastName}, ${designation[0].desc}`;
  }else if(salutation.length !==0  && designation.length === 0){
    return `${salutation[0].desc} ${firstName} ${(middleName ? middleName: ' ')} ${lastName}`;
  }else if( salutation.length === 0 && designation.length !== 0){
    return `${firstName} ${(middleName? middleName: ' ')} ${lastName}, ${designation[0].desc}`;
  }else{
    return `${firstName} ${(middleName? middleName: ' ')} ${lastName}`;
  }
};

export const isAllocationValuePercent = type => type === "By specific percentage";

  //** Finds the total width of the screen and automatically align the tooltip in mobile as well as in desktop */
export const tooltipAligner = (idName) => {

  const windowWidth = window.innerWidth;
  
  if(windowWidth < 991) {
    let documentDetail = document.querySelector(idName);
    if(documentDetail) {
      let currentPosition = documentDetail.getBoundingClientRect();
      if(currentPosition.left < 140 && (windowWidth - currentPosition.left) > 250) return "topRight"
      else if((windowWidth - currentPosition.left) < 140 && currentPosition.left > 250) return "leftTop"
      else return "top"     
    }
  } else {
    return "right";
  }
};

export default prepareRender;
